import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox, Table, TableBody, TableCell, TableHead, TableRow } from '@material-ui/core';

import { Icon } from '@nearst/ui';

import { hideProducts, useInventory } from '@services/stock';
import { getChannelsForStockItems } from '@services/stockChannels';
import { useShop } from '@services/ShopProvider';

import { useIsChain } from '@utils/useIsChain';

import EditChannelVisibilityModal from '../../components/EditChannelVisibilityModal';
import { Issues } from './TableCells/Issues';
import { Status } from './TableCells/Status';
import { Item } from './TableCells/Item';
import { Price } from './TableCells/Price';
import { Category } from './TableCells/Category';
import { Quantity } from './TableCells/Quantity';
import { MenuCategories } from './TableCells/MenuCategories';
import { NavigationCell } from './TableCells/NavigationCell';
import { SelectedBarcodesActionBar } from './SelectedBarcodesActionBar';

import styles from './InventoryViewer.module.scss';

const DesktopTable = ({ setDisableActions, setStockLevelsBarcode, channels, hasLastMileChannels }) => {
	const { shop, organisation, data: shops } = useShop();
	const isChain = useIsChain();
	const { inventory, optimisticUpdate } = useInventory(organisation.id, shop.id);
	const [selectedBarcodes, setSelectedBarcodes] = useState([]);
	const isAllSelected = selectedBarcodes?.length === inventory?.length;

	useEffect(() => {
		if (selectedBarcodes.length >= 1) setDisableActions(true);
		if (selectedBarcodes.length === 0) setDisableActions(false);
	}, [selectedBarcodes]);

	const [editChannelsModalShown, setEditChannelsModalShown] = useState(false);

	const stockItems = inventory?.filter(({ barcode }) => selectedBarcodes.includes(barcode));
	const channelStatuses =
		editChannelsModalShown && selectedBarcodes?.length ? getChannelsForStockItems(shop.id, stockItems, channels) : [];

	const hideMode = useMemo(() => stockItems?.some((item) => item.enabled), [stockItems]);

	const handleSelectAll = () => {
		setSelectedBarcodes(inventory.map((row) => row.barcode));
	};

	const handleDeselectAll = () => {
		setSelectedBarcodes([]);
	};

	const markHidden = (enabled) => {
		optimisticUpdate(
			(inventory) => inventory.map((item) => (selectedBarcodes.includes(item.barcode) ? { ...item, enabled_i: enabled } : item)),
			() => hideProducts(isChain ? undefined : shop.id, selectedBarcodes, enabled)
		);
		setSelectedBarcodes([]);
	};

	return (
		<>
			<EditChannelVisibilityModal
				stockItems={stockItems}
				shopIds={isChain ? shops.map((s) => s.id) : [shop.id]}
				channelStatuses={channelStatuses}
				onDismiss={() => {
					setSelectedBarcodes([]);
					return setEditChannelsModalShown(false);
				}}
				isOpen={editChannelsModalShown}
			/>
			<SelectedBarcodesActionBar
				selectedBarcodes={selectedBarcodes}
				setEditChannelsModalShown={setEditChannelsModalShown}
				markHidden={markHidden}
				hideMode={hideMode}
			/>
			<Table className={styles.desktopTable} role="table">
				<TableHead>
					<TableRow>
						<TableCell>
							<Checkbox
								data-cy="inventory-table-checkbox-all"
								color="primary"
								className={styles.checkbox}
								indeterminate={selectedBarcodes.length && !isAllSelected}
								checked={isAllSelected}
								onChange={() => (isAllSelected ? handleDeselectAll() : handleSelectAll())}
								inputProps={{
									'aria-label': 'select all products'
								}}
							/>
						</TableCell>
						<TableCell>Item</TableCell>
						<TableCell>Price</TableCell>
						<TableCell>Quantity</TableCell>
						<TableCell>Category</TableCell>
						{hasLastMileChannels && <TableCell>Menu categories</TableCell>}
						{!isChain && <TableCell>Status</TableCell>}
						<TableCell>Issues</TableCell>
						<TableCell />
					</TableRow>
				</TableHead>
				<TableBody>
					{inventory &&
						inventory.map((inventory, index) => {
							const row = {
								...inventory,
								hidden: !inventory.enabled,
								edited: inventory.productSource === 'myshop',
								quantity: inventory.quantity_i,
								hasIssues: inventory.issues?.length > 0,
								labelId: `inventory-table-checkbox-${index}`,
								isSelected: selectedBarcodes.includes(inventory.barcode)
							};

							return (
								<TableRow
									className={styles.clickMe}
									key={row.barcode}
									data-cy="inventory-table-row"
									data-barcode={row.barcode}
								>
									<Item row={row} setSelectedBarcodes={setSelectedBarcodes} />
									<Price row={row} />

									<Quantity onClick={() => setStockLevelsBarcode(row.barcode)} row={row} />
									<Category row={row} />
									{hasLastMileChannels && <MenuCategories row={row} />}

									<Status row={row} />

									<Issues row={row} />
									{!isChain && (
										<NavigationCell barcode={row.barcode}>
											<Icon className={styles.summaryIcon}>chevron_right</Icon>
										</NavigationCell>
									)}
								</TableRow>
							);
						})}
				</TableBody>
			</Table>
		</>
	);
};

export default DesktopTable;
